<template>
  <div id="tool-editor">
    <div class="tool-fields">
      <div class="save-buttons">
        <button type="button" class="btn btn-primary btn" @click="reset">
          <v-icon small>repeat</v-icon>
        </button>
        <button type="button" class="btn btn-success btn" @click="save">
          <span v-if="tool.id">{{$t('tools_table.save')}}</span>
          <span v-else>{{$t('tools_table.create_new')}}</span>
        </button>
      </div>
      <div class="form-group">
        <label for="codeInput">{{$t('tools_table.code')}}</label>
        <input
          v-model="tool.code"
          type="text"
          class="form-control"
          id="codeInput"
          :placeholder="$t('tools_table.code')"
        />
      </div>
      <div class="form-group">
        <label for="descriptionInput">{{$t('tools_table.description')}}</label>
        <input
          v-model="tool.description"
          type="text"
          class="form-control"
          id="descriptionInput"
          :placeholder="$t('tools_table.description')"
        />
      </div>
      <div class="form-row">
        <div class="col">
          <label for="lengthInput">{{$t('tools_table.length')}}</label>
          <input
            v-model="tool.length"
            id="lengthInput"
            type="number"
            step="0.01"
            class="form-control"
            :placeholder="$t('tools_table.length')"
          />
        </div>
        <div class="col">
          <label for="diameterInput">{{$t('tools_table.diameter')}}</label>
          <input
            v-model="tool.diameter"
            id="diameterInput"
            type="number"
            step="0.01"
            class="form-control"
            :placeholder="$t('tools_table.diameter')"
          />
        </div>
      </div>
      <div class="text-editor">
        <label for="geometryInput">{{$t('tools_table.geometry')}}</label>
        <TextEditor
          id="geometryInput"
          ref="textEditor"
          :big="true"
          :startingCode="tool.geometry"
          @codeChanged="updateGeometry"
        ></TextEditor>
      </div>
      <div id="picture-wrapper" class="picture-wrapper">
        <canvas id="tool-canvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import TextEditor from "@/components/TextEditor.vue";
import Tool from "@/models/Tool.js";
import Helper from "@/models/Helper.js";
export default {
  name: "tool-editor",
  components: {
    TextEditor,
  },
  data() {
    return {
      tool: {},
      scale: 1,
      selectedTool: false,
      firstTime: true
    };
  },
  watch: {
    "tool.length"() {
      this.draw(true);
    },
    "tool.diameter"() {
      this.draw(true);
    },
  },
  created() {
    window.addEventListener("resize", this.resizeHandler);
  },
  destroyed() {
    window.removeEventListener("resize", this.resizeHandler);
  },
  methods: {
    resizeHandler() {
      if (this.selectedTool) {
        this.draw(true);
      }
    },
    reset() {
      this.tool = {};
      this.selectedTool = false;
      this.$refs.textEditor.changeCode("");
    },
    selectTool(tool) {
      this.selectedTool = true;
      this.tool = new Tool(tool);
      this.$refs.textEditor.changeCode(tool.geometry);
    },
    updateGeometry(geometry) {
      this.tool.geometry = geometry;
      this.draw(false);
    },
    async save() {
      if (this.tool.id) {
        const updated = await this.tool.save();
        if (!updated) {
          alert(this.$t("tools_table.all_fields_required"));
          return;
        }
        this.$emit("updated");
      } else {
        const created = await Tool.create(this.tool);
        if (!created) {
          alert(this.$t("tools_table.all_fields_required"));
          return;
        }
        this.$emit("updated");
      }
    },
    draw(shouldRescale) {
      if (this.tool.geometry) {
        const canvas = document.getElementById("tool-canvas");
        canvas.width = window.innerWidth > 1400 ? 500 : 300;
        canvas.height = window.innerWidth > 1400 ? 500 : 300;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "rgb(240, 240, 240)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const border = 20;
        this.drawDimensions(canvas, ctx, border);
        let coordinates = this.getCoordinates(canvas, this.scale, border);
        if (shouldRescale || this.firstTime) {
          this.firstTime = false;
          coordinates = this.scaleCoordinates(canvas, border);
        }
        this.drawProfile(ctx, coordinates);
        this.drawMesh(canvas, ctx, border);
      }
    },

    drawMesh(canvas, ctx, border) {
      ctx.strokeStyle = "gray";
      ctx.lineWidth = 0.2;
      const density = 10 * this.scale;
      const width = canvas.width - 2 * border;
      const height = canvas.height - 2 * border;
      let x = canvas.width - border;
      let y = border;
      for (let i = 0; i < width / density + 1; i++) {
        if(i > 1000){
          break;
        }
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + height);
        x -= density;
      }
      x = border;
      y = border;
      for (let i = 0; i < height / density + 1; i++) {
        if(i > 1000){
          break;
        }
        ctx.moveTo(x, y);
        ctx.lineTo(x + width, y);
        y += density;
      }
      ctx.stroke();
    },

    drawDimensions(canvas, ctx, border) {
      ctx.beginPath();
      ctx.lineWidth = 1;
      ctx.strokeStyle = "black";
      Helper.canvasArrow(
        ctx,
        canvas.width - border,
        border,
        (canvas.width - 2 * border) / 2,
        border
      );
      Helper.canvasArrow(
        ctx,
        canvas.width - border,
        border,
        canvas.width - border,
        (canvas.height - 2 * border) / 2
      );
      ctx.strokeText("x", (canvas.width - 2 * border) / 2 - 10, border);
      ctx.strokeText(
        "y",
        canvas.width - border,
        (canvas.height - 2 * border) / 2 + 10
      );
    },

    drawProfile(ctx, coordinates) {
      ctx.lineWidth = 1;
      for (let i = 0; i < coordinates.length - 1; i++) {
        ctx.beginPath();
        ctx.strokeStyle = "blue";
        const x1 = coordinates[i].x;
        const y1 = coordinates[i].y;
        const x2 = coordinates[i + 1].x;
        const y2 = coordinates[i + 1].y;
        const radius = Math.abs(coordinates[i + 1].r);
        const counterClockwise = coordinates[i + 1].r < 0;
        if (coordinates[i + 1].r == null) {
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
        } else {
          let centerX, centerY;
          if (counterClockwise) {
            centerX = this.centerX(x1, y1, x2, y2, radius);
            centerY = this.centerY(x1, y1, x2, y2, radius);
          } else {
            centerX = this.centerX(x2, y2, x1, y1, radius);
            centerY = this.centerY(x2, y2, x1, y1, radius);
          }
          const startAngle = Math.atan2(y1 - centerY, x1 - centerX);
          const endAngle = Math.atan2(y2 - centerY, x2 - centerX);
          ctx.beginPath();
          ctx.arc(
            centerX,
            centerY,
            radius,
            startAngle,
            endAngle,
            !counterClockwise
          );
        }
        ctx.stroke();
        ctx.strokeText(i + 1, x1, y1);
      }
      if (coordinates[coordinates.length - 1]) {
        ctx.strokeText(
          coordinates.length,
          coordinates[coordinates.length - 1].x,
          coordinates[coordinates.length - 1].y
        );
      }
    },

    getCoordinates(canvas, multiplier, border) {
      if (!this.tool.geometry) return;
      const points = this.tool.geometry.split("\n");
      let startingX = canvas.width - border;
      let startingY = 0 + border;
      const convertedCoordinates = [];
      for (let i = 0; i < points.length; i++) {
        const coordinates = points[i].split(" ");
        const x = startingX + (coordinates.length >= 1 ? coordinates[0] * -1 * multiplier : null);
        const y = startingY + (coordinates.length >= 2 ? coordinates[1] * multiplier : null);
        const r = coordinates.length >= 3 ? parseInt(coordinates[2]) * multiplier : null;
        convertedCoordinates.push({
          x: x,
          y: y,
          r: r
        })
      }
      return convertedCoordinates;
    },

    scaleCoordinates(canvas, border) {
      const width = canvas.width - 2 * border;
      const height = canvas.height - 2 * border;
      const toolWidth = this.tool.diameter > 0 ? this.tool.diameter / 2 : 1;
      const toolHeight = this.tool.length > 0 ? this.tool.length : 1;
      const smallerScale = Math.min(width / toolWidth, height / toolHeight);
      this.scale = smallerScale;
      return this.getCoordinates(canvas, smallerScale, border);
    },

    centerX(x1, y1, x2, y2, radius) {
      const radsq = radius * radius;
      const q = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
      const x3 = (x1 + x2) / 2;
      return x3 + Math.sqrt(radsq - (q / 2) * (q / 2)) * ((y1 - y2) / q);
    },

    centerY(x1, y1, x2, y2, radius) {
      const radsq = radius * radius;
      const q = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
      const y3 = (y1 + y2) / 2;
      return y3 + Math.sqrt(radsq - (q / 2) * (q / 2)) * ((x2 - x1) / q);
    },
  },
};
</script>

<style scoped>
.tool-fields {
  position: relative;
  padding-top: 5px;
  top: -20px;
  left: -10px;
  width: 100%;
}
.tool-picture {
  position: relative;
  width: 100%;
  height: 30%;
  left: -10px;
  top: -20px;
  padding-top: 30px;
}
.text-editor {
  margin-left: 20px;
  margin-top: 20px;
}
.picture-wrapper {
  align-content: center;
  position: relative;
  float: right;
  top: -300px;
  right: 10px;
}
@media only screen and (max-width: 1400px) {
  .picture-wrapper {
    width: 300px;
    min-height: 300px;
  }
}
@media only screen and (min-width: 1401px) {
  .picture-wrapper {
    width: 500px;
    min-height: 500px;
  }
}
</style>